import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { BASE_URL } from "../services/configUrls";
import axios from "axios";

/**
 * Determines which popup to show based on awards object:
 *   awards = {}                  -> show nothing
 *   any value === "1"            -> "winner" popup
 *   all values === "2"           -> "thankyou" popup
 *   mix of "1" & "2"            -> "winner" popup (at least one won)
 *   other values                 -> show nothing
 */
const resolvePopupType = (awards) => {
  if (!awards || typeof awards !== "object") return null;
  const values = Object.values(awards);
  if (values.length === 0) return null;
  const hasWinner = values.some((v) => String(v) === "1");
  const allThanks = values.every((v) => String(v) === "2");
  if (hasWinner) return "winner";
  if (allThanks) return "thankyou";
  return null;
};

const AwardWinnerPopup = () => {
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [popupType, setPopupType] = useState(null);
  const [winnerAwards, setWinnerAwards] = useState([]);

  useEffect(() => {
    const checkAwardWinner = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) return;

        const response = await axios.get(`${BASE_URL}/awards/award_winners`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = response.data;
        if (!data) return;

        const awards = data.awards;
        const type = resolvePopupType(awards);
        if (!type) return;

        setFullName(data.full_name || "");
        setPopupType(type);

        if (type === "winner" && awards) {
          const won = Object.entries(awards)
            .filter(([, v]) => String(v) === "1")
            .map(([name]) => name);
          setWinnerAwards(won);
        }

        setOpen(true);
      } catch (error) {
        console.error("Error fetching award winners:", error);
      }
    };

    checkAwardWinner();
  }, []);

  const handleClose = () => setOpen(false);

  if (!open || !popupType) return null;

  /* Winner Popup */
  if (popupType === "winner") {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            background: "linear-gradient(135deg, #fffbeb 0%, #fff7cd 50%, #fffbeb 100%)",
            boxShadow: "0 24px 60px rgba(251,191,36,0.25)",
          },
        }}
      >
        <Box sx={{ height: 6, background: "linear-gradient(90deg, #f59e0b, #fbbf24, #f59e0b)" }} />
        <DialogContent sx={{ pt: 4, pb: 4, px: 4, textAlign: "center" }}>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ position: "absolute", right: 12, top: 12, color: "#92400e" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 96,
              height: 96,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
              boxShadow: "0 8px 30px rgba(245,158,11,0.4)",
              mb: 3,
              animation: "trophyPulse 2s infinite",
              "@keyframes trophyPulse": {
                "0%, 100%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.06)" },
              },
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: 52, color: "#fff" }} />
          </Box>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{
              background: "linear-gradient(135deg, #d97706, #f59e0b)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 0.5,
            }}
          >
            Congratulations!
          </Typography>
          <Typography variant="h6" fontWeight={700} color="#1e293b" mb={1}>
            {fullName}
          </Typography>
          <Typography variant="body1" color="#64748b" mb={3}>
            You have won the following award{winnerAwards.length > 1 ? "s" : ""}:
          </Typography>
          <Box display="flex" flexDirection="column" gap={1.5} mb={4}>
            {winnerAwards.map((name, i) => (
              <Box
                key={i}
                sx={{
                  background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                  border: "1.5px solid #fbbf24",
                  borderRadius: 2.5,
                  px: 3,
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <EmojiEventsIcon sx={{ color: "#d97706", fontSize: 22, flexShrink: 0 }} />
                <Typography fontWeight={700} color="#92400e" fontSize={15} textAlign="left">
                  {name}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            variant="contained"
            onClick={handleClose}
            size="large"
            sx={{
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              borderRadius: 3,
              px: 5,
              py: 1.5,
              fontWeight: 700,
              fontSize: 15,
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(245,158,11,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706, #b45309)",
                boxShadow: "0 6px 20px rgba(245,158,11,0.5)",
              },
            }}
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  /* Thank You Popup (all awards === "2") */
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          background: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
          boxShadow: "0 24px 60px rgba(14,165,233,0.2)",
        },
      }}
    >
      <Box sx={{ height: 6, background: "linear-gradient(90deg, #0ea5e9, #38bdf8, #0ea5e9)" }} />
      <DialogContent sx={{ pt: 4, pb: 4, px: 4, textAlign: "center" }}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", right: 12, top: 12, color: "#0369a1" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 96,
            height: 96,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #38bdf8, #0ea5e9)",
            boxShadow: "0 8px 30px rgba(14,165,233,0.35)",
            mb: 3,
          }}
        >
          <FavoriteIcon sx={{ fontSize: 48, color: "#fff" }} />
        </Box>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            background: "linear-gradient(135deg, #0284c7, #0ea5e9)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 0.5,
          }}
        >
          Thank You!
        </Typography>
        <Typography variant="h6" fontWeight={700} color="#1e293b" mb={1}>
          {fullName}
        </Typography>
        <Typography variant="body1" color="#64748b" mb={2} sx={{ maxWidth: 360, mx: "auto" }}>
          Thank you for participating in the Special Category Awards. Your contribution means a lot to us!
        </Typography>
        <Chip
          label="Participation Appreciated"
          sx={{
            background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
            color: "#0369a1",
            fontWeight: 700,
            fontSize: 13,
            px: 1,
            mb: 3,
            border: "1px solid #7dd3fc",
          }}
        />
        <Box>
          <Button
            variant="contained"
            onClick={handleClose}
            size="large"
            sx={{
              background: "linear-gradient(135deg, #0ea5e9, #0284c7)",
              borderRadius: 3,
              px: 5,
              py: 1.5,
              fontWeight: 700,
              fontSize: 15,
              textTransform: "none",
              boxShadow: "0 4px 15px rgba(14,165,233,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #0284c7, #0369a1)",
                boxShadow: "0 6px 20px rgba(14,165,233,0.5)",
              },
            }}
          >
            Close
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AwardWinnerPopup;
